import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import useIsMobile from '../hooks/useIsMobile'
import { localDateStr } from '../utils/date'
import { legalLeaveDays, tenureText } from '../utils/leave'

/**
 * Edge Function manage-users 호출 (fetch + apikey + JWT)
 */
async function invokeManageUsers(body) {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession()
  if (sessionError || !session?.access_token) {
    return {
      data: null,
      error: Object.assign(new Error('로그인이 만료되었습니다. 다시 로그인해 주세요.'), { skipRead: true }),
    }
  }

  const base = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '')
  const anon = process.env.REACT_APP_SUPABASE_ANON_KEY || ''
  if (!base || !anon) {
    return {
      data: null,
      error: Object.assign(new Error('.env에 REACT_APP_SUPABASE_URL·REACT_APP_SUPABASE_ANON_KEY를 넣었는지 확인하세요'), { skipRead: true }),
    }
  }

  let res
  try {
    res = await fetch(`${base}/functions/v1/manage-users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anon,
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    })
  } catch (e) {
    return {
      data: null,
      error: Object.assign(new Error(e.message || '네트워크 오류'), { skipRead: true }),
    }
  }

  const text = await res.text()
  let json = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    json = { error: text || res.statusText }
  }

  if (!res.ok) {
    const msg = (json && json.error) ? String(json.error) : (text || `HTTP ${res.status}`)
    return {
      data: json,
      error: Object.assign(new Error(msg), { skipRead: true }),
    }
  }

  return { data: json, error: null }
}

/** Edge Function이 4xx/5xx일 때 본문의 { error } 를 꺼내 표시 */
async function readInvokeError(error) {
  const res = error?.context
  if (res && typeof res.json === 'function') {
    try {
      const body = await res.clone().json()
      if (body?.error) return String(body.error)
    } catch (_) { /* 본문 없음 */ }
  }
  return error?.message ?? '요청 실패'
}

export default function Members({ profile }) {
  const [members, setMembers] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ email:'', password:'', name:'', dept:'', quota:15, hire_date:'' })
  const [loading, setLoading] = useState(false)
  const [editTarget, setEditTarget] = useState(null)      // 수정 중인 직원
  const [editForm, setEditForm] = useState({ hire_date:'', quota:15 })
  const [showPayModal, setShowPayModal] = useState(false) // 연차수당 지급
  const [payouts, setPayouts] = useState([])
  const [payDate, setPayDate] = useState(() => localDateStr())
  const isMobile = useIsMobile()

  useEffect(() => { fetchMembers(); fetchPayouts() }, [])

  async function fetchMembers() {
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: true })
    if (data) setMembers(data)
  }

  async function fetchPayouts() {
    const { data, error } = await supabase.from('leave_payouts').select('*').order('pay_date', { ascending: false }).limit(50)
    if (error) console.error('leave_payouts 조회:', error.message)
    if (data) setPayouts(data)
  }

  /** 입사일 입력 시 법정 연차 자동 반영 (직접 수정 가능) */
  function onHireDateChange(v) {
    const legal = legalLeaveDays(v)
    setForm(f => ({ ...f, hire_date: v, quota: legal ?? f.quota }))
  }

  function openEditModal(m) {
    setEditTarget(m)
    setEditForm({ hire_date: m.hire_date || '', quota: m.leave_quota ?? 15 })
  }

  async function saveMemberEdit() {
    if (!editTarget) return
    const { error } = await supabase.from('profiles').update({
      hire_date: editForm.hire_date || null,
      leave_quota: Number(editForm.quota) || 0,
    }).eq('id', editTarget.id)
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nprofiles에 hire_date 컬럼이 있는지 확인하세요 (supabase/sql/leave_payouts.sql 실행)')
      return
    }
    setEditTarget(null)
    fetchMembers()
  }

  /** 연차수당 지급: 기록 추가 + 사용일수에 가산(잔여에서 차감) */
  async function payAllowance(emp, days, note) {
    const d = Number(days)
    if (!Number.isFinite(d) || d <= 0) { alert('지급 일수를 입력하세요'); return }
    const remain = (emp.leave_quota ?? 0) - (emp.leave_used ?? 0)
    if (d > remain && !window.confirm(`${emp.name}의 잔여 연차(${remain}일)보다 많습니다. 그래도 지급할까요?`)) return
    const { error: insErr } = await supabase.from('leave_payouts').insert({
      emp_id: emp.id,
      emp_name: emp.name,
      pay_date: payDate || localDateStr(),
      days: d,
      note: note || '',
      created_by: profile.id,
      created_by_name: profile.name,
    })
    if (insErr) {
      alert('지급 기록 실패: ' + insErr.message + '\n\nleave_payouts 테이블이 있는지 확인하세요 (supabase/sql/leave_payouts.sql 실행)')
      return
    }
    await supabase.from('profiles').update({ leave_used: (emp.leave_used ?? 0) + d }).eq('id', emp.id)
    fetchMembers()
    fetchPayouts()
  }

  /** 지급 취소: 기록 삭제 + 차감했던 일수 복원 */
  async function cancelPayout(p) {
    if (!window.confirm(`${p.emp_name} · ${p.days}일 (${p.pay_date}) 지급을 취소할까요?\n차감했던 연차가 복원됩니다.`)) return
    const { error } = await supabase.from('leave_payouts').delete().eq('id', p.id)
    if (error) { alert('취소 실패: ' + error.message); return }
    const { data: emp } = await supabase.from('profiles').select('leave_used').eq('id', p.emp_id).single()
    if (emp) {
      await supabase.from('profiles').update({ leave_used: Math.max(0, (emp.leave_used ?? 0) - Number(p.days)) }).eq('id', p.emp_id)
    }
    fetchMembers()
    fetchPayouts()
  }

  async function addMember() {
    if (!form.email || !form.password || !form.name) {
      alert('이메일, 비밀번호, 이름은 필수입니다'); return
    }
    setLoading(true)
    try {
      const { data, error } = await invokeManageUsers({
        action: 'create',
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        dept: form.dept.trim(),
        quota: Number(form.quota),
        hire_date: form.hire_date || null,
      })
      if (error) {
        alert('오류: ' + (error.skipRead ? error.message : await readInvokeError(error)))
        setLoading(false)
        return
      }
      if (data?.error) {
        alert('오류: ' + data.error)
        setLoading(false)
        return
      }

      const addedName = form.name
      setShowModal(false)
      setForm({ email:'', password:'', name:'', dept:'', quota:15, hire_date:'' })
      fetchMembers()
      alert(`${addedName} 계정이 생성되었습니다!`)
    } catch (e) {
      alert('오류: ' + (e.message || String(e)))
    }
    setLoading(false)
  }

  async function deleteMember(id, name) {
    if (!window.confirm(`${name} 계정을 삭제할까요?`)) return
    const { data, error } = await invokeManageUsers({ action: 'delete', userId: id })
    if (error) {
      const msg = error.skipRead ? error.message : await readInvokeError(error)
      alert('오류: ' + msg)
      return
    }
    if (data?.error) {
      alert('오류: ' + data.error)
      return
    }
    fetchMembers()
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>직원 관리</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>직원 계정 추가 · 삭제 · 연차 현황</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowPayModal(true)}
            style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e5e3de', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            💰 연차수당
          </button>
          <button onClick={() => setShowModal(true)}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            + 직원 추가
          </button>
        </div>
      </div>

      {isMobile ? (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {members.map(m => {
            const remain = m.leave_quota - m.leave_used
            return (
              <div key={m.id} style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:14, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                    <span style={{ fontSize:14, fontWeight:700 }}>{m.name}</span>
                    <span style={{
                      padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:500, flexShrink:0,
                      background: m.role === 'admin' ? '#e8f2eb' : '#e8eef8',
                      color: m.role === 'admin' ? '#2d5a3d' : '#1e4a8c'
                    }}>
                      {m.role === 'admin' ? '관리자' : '직원'}
                    </span>
                  </div>
                  {m.role !== 'admin' && (
                    <button onClick={() => deleteMember(m.id, m.name)}
                      style={{ padding:'4px 10px', border:'none', borderRadius:6, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12, flexShrink:0 }}>
                      삭제
                    </button>
                  )}
                </div>
                <div style={{ fontSize:12, color:'#6b6860', marginBottom:4 }}>
                  {m.dept || '부서 미지정'}{m.email ? ' · ' + m.email : ''}
                </div>
                {m.hire_date && (
                  <div style={{ fontSize:12, color:'#6b6860', marginBottom:4 }}>
                    입사 {m.hire_date} <span style={{ color:'#a09e99' }}>({tenureText(m.hire_date)})</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:12 }}>
                    연차 잔여 <span style={{ fontWeight:700, color: remain <= 3 ? '#b83030' : '#2d5a3d' }}>{remain}일</span>
                    <span style={{ color:'#a09e99' }}> / {m.leave_quota}일</span>
                  </span>
                  <button onClick={() => openEditModal(m)}
                    style={{ padding:'4px 10px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>
                    ✏️ 수정
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['이름','부서','이메일','입사일','권한','연차(잔여/총)',''].map(h => (
                <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                <td style={{ padding:'12px 14px', fontWeight:600 }}>{m.name}</td>
                <td style={{ padding:'12px 14px', color:'#6b6860' }}>{m.dept}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{m.email || '—'}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', whiteSpace:'nowrap' }}>
                  {m.hire_date ? (
                    <>
                      {m.hire_date}
                      <div style={{ fontSize:10, color:'#a09e99' }}>{tenureText(m.hire_date)}</div>
                    </>
                  ) : '—'}
                </td>
                <td style={{ padding:'12px 14px' }}>
                  <span style={{
                    padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500,
                    background: m.role === 'admin' ? '#e8f2eb' : '#e8eef8',
                    color: m.role === 'admin' ? '#2d5a3d' : '#1e4a8c'
                  }}>
                    {m.role === 'admin' ? '관리자' : '직원'}
                  </span>
                </td>
                <td style={{ padding:'12px 14px' }}>
                  <span style={{ fontWeight:700, color: (m.leave_quota - m.leave_used) <= 3 ? '#b83030' : '#2d5a3d' }}>
                    {m.leave_quota - m.leave_used}일
                  </span>
                  <span style={{ fontSize:12, color:'#a09e99' }}> / {m.leave_quota}일</span>
                </td>
                <td style={{ padding:'12px 14px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => openEditModal(m)}
                      style={{ padding:'4px 10px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>
                      수정
                    </button>
                    {m.role !== 'admin' && (
                      <button onClick={() => deleteMember(m.id, m.name)}
                        style={{ padding:'4px 10px', border:'none', borderRadius:6, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12 }}>
                        삭제
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* 직원 추가 모달 */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:460, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>직원 추가</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>이름 *</label>
                  <input value={form.name} onChange={e => setForm({...form, name:e.target.value})}
                    placeholder="홍길동"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>부서</label>
                  <input value={form.dept} onChange={e => setForm({...form, dept:e.target.value})}
                    placeholder="생산, 관리 등"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>이메일 *</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})}
                  placeholder="example@email.com"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>초기 비밀번호 *</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password:e.target.value})}
                  placeholder="6자리 이상"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>입사일</label>
                  <input type="date" value={form.hire_date} onChange={e => onHireDateChange(e.target.value)}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>연차 부여일수</label>
                  <input type="number" value={form.quota} onChange={e => setForm({...form, quota:e.target.value})}
                    min={0}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                  {form.hire_date && (
                    <div style={{ fontSize:11, color:'#4a8c60', marginTop:4 }}>
                      법정 연차 {legalLeaveDays(form.hire_date)}일 자동 입력됨 (수정 가능)
                    </div>
                  )}
                </div>
              </div>
              <div style={{ background:'#fff3e0', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#c0601a' }}>
                💡 직원에게 이메일과 초기 비밀번호를 알려주세요. 로그인 후 비밀번호 변경 가능합니다.
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={addMember} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '추가 중...' : '추가하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 직원 수정 모달 (입사일 · 연차) */}
      {editTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setEditTarget(null)}>
          <div style={{ background:'#fff', borderRadius:14, width:400, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editTarget.name} 정보 수정</span>
              <button onClick={() => setEditTarget(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>입사일</label>
                <input type="date" value={editForm.hire_date} onChange={e => setEditForm({...editForm, hire_date:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                {editForm.hire_date && (
                  <div style={{ fontSize:11, color:'#a09e99', marginTop:4 }}>근속 {tenureText(editForm.hire_date)}</div>
                )}
              </div>
              <div style={{ marginBottom:6 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>총 연차일수</label>
                <input type="number" min={0} value={editForm.quota} onChange={e => setEditForm({...editForm, quota:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
              </div>
              {editForm.hire_date && legalLeaveDays(editForm.hire_date) !== null && (
                <div style={{ display:'flex', alignItems:'center', gap:8, background:'#e8f2eb', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#2d5a3d' }}>
                  <span style={{ flex:1 }}>입사일 기준 법정 연차: <b>{legalLeaveDays(editForm.hire_date)}일</b></span>
                  <button onClick={() => setEditForm(f => ({ ...f, quota: legalLeaveDays(f.hire_date) }))}
                    style={{ padding:'4px 12px', border:'none', borderRadius:6, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:11, fontWeight:600 }}>
                    적용
                  </button>
                </div>
              )}
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setEditTarget(null)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={saveMemberEdit}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 연차수당 지급 모달 */}
      {showPayModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowPayModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:560, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700 }}>💰 연차수당 지급</div>
                <div style={{ fontSize:11, color:'#a09e99', marginTop:2 }}>지급한 일수만큼 잔여 연차에서 차감됩니다 · 잘못 지급하면 내역에서 취소</div>
              </div>
              <button onClick={() => setShowPayModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>지급일</label>
                <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                  style={{ padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit' }} />
              </div>

              {/* 직원별 지급 */}
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:20 }}>
                <thead>
                  <tr>
                    {['직원','잔여','지급 일수',''].map(h => (
                      <th key={h} style={{ padding:'8px 10px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <PayRow key={m.id} emp={m} onPay={payAllowance} />
                  ))}
                </tbody>
              </table>

              {/* 지급 내역 */}
              <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>최근 지급 내역</div>
              {payouts.length === 0 ? (
                <div style={{ fontSize:12, color:'#a09e99', padding:'12px 0' }}>지급 내역이 없습니다</div>
              ) : (
                payouts.map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #f0eeea' }}>
                    <span style={{ fontSize:12, color:'#a09e99', flexShrink:0 }}>{p.pay_date}</span>
                    <span style={{ fontSize:13, fontWeight:600, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {p.emp_name} <span style={{ fontWeight:400, color:'#6b6860' }}>· {Number(p.days)}일 차감</span>
                    </span>
                    <button onClick={() => cancelPayout(p)}
                      style={{ padding:'3px 10px', border:'none', borderRadius:6, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:11, flexShrink:0 }}>
                      취소
                    </button>
                  </div>
                ))
              )}
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => setShowPayModal(false)}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PayRow({ emp, onPay }) {
  const [days, setDays] = useState('')
  const remain = (emp.leave_quota ?? 0) - (emp.leave_used ?? 0)
  return (
    <tr style={{ borderBottom:'1px solid #f0eeea' }}>
      <td style={{ padding:'9px 10px', fontWeight:600, fontSize:13 }}>{emp.name}</td>
      <td style={{ padding:'9px 10px', fontSize:13 }}>
        <span style={{ fontWeight:700, color: remain <= 3 ? '#b83030' : '#2d5a3d' }}>{remain}일</span>
      </td>
      <td style={{ padding:'6px 10px' }}>
        <input type="number" min={0} step={0.5} value={days} onChange={e => setDays(e.target.value)}
          placeholder="0"
          style={{ width:70, padding:'6px 8px', border:'1px solid #e5e3de', borderRadius:6, fontSize:13, textAlign:'center', fontFamily:'inherit' }} />
      </td>
      <td style={{ padding:'6px 10px' }}>
        <button onClick={() => { onPay(emp, days); setDays('') }}
          disabled={!Number(days)}
          style={{
            padding:'5px 14px', border:'none', borderRadius:6, fontSize:12, fontWeight:600,
            background: Number(days) ? '#2d5a3d' : '#e5e3de',
            color: Number(days) ? '#fff' : '#a09e99',
            cursor: Number(days) ? 'pointer' : 'default',
          }}>
          지급
        </button>
      </td>
    </tr>
  )
}
